import { Dropdown } from "antd";
import { type DropDownProps } from "antd/lib/dropdown";
import { type MenuInfo } from "rc-menu/lib/interface";
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from "react";

import { useMenuFocusOnOpen } from "@btnet/blueprint-components";
import { cn } from "@btnet/utilities-core";

import { useTracking } from "utilities/analytics/analytics";
import { getClosestModal } from "utilities/helpers";

import "./BTDropdown.less";

/**
 * BTDropdown props - extends antd DropDownProps with testid and triggerRef.
 * Provides full access to antd's Dropdown API.
 */
export interface IBTDropdownProps extends DropDownProps {
    children?: ReactNode;
    /** Required test identifier for testing and analytics. */
    testid: string;
    /**
     * Ref to the trigger element. When provided, focus returns here when the menu closes.
     * Wire the same ref to the trigger element (e.g. BdsButton's `innerRef`) for focus return.
     */
    triggerRef?: RefObject<HTMLElement | null>;
}

/** rc-dropdown fires `onOverlayClick` with a MenuInfo object. `domEvent.target` is narrowed
 * here so callers don't need a cast — React synthetic events type `.target` as `EventTarget`,
 * not `HTMLElement`.
 * `domEvent` is optional because non-Menu overlays (or certain rc-dropdown code paths) can
 * fire `onOverlayClick` with a plain DOM event that has no `domEvent` property. */
type IRCOverlayClickEvent = Omit<MenuInfo, "domEvent"> & {
    domEvent?: { target: HTMLElement | null };
};

const defaultTriggers: IBTDropdownProps["trigger"] = ["click"];
const defaultAlign: IBTDropdownProps["align"] = { offset: [0, 0] };

/**
 * BTDropdown component - a managed dropdown wrapper around antd Dropdown.
 * Handles visibility state, analytics tracking, and keyboard accessibility internally.
 *
 * Built-in behavior:
 * - Click trigger to open/close
 * - Escape key closes dropdown
 * - Focus transfers to menu on open (for keyboard navigation)
 * - Focus returns to trigger on close (when triggerRef is provided)
 * - Menu closes on item click
 * - Analytics tracking on open
 *
 * For keyboard focus return, pass a `triggerRef` and wire the same ref to the trigger element.
 * @param props - The component props.
 * @param props.testid - Test ID for testing and analytics.
 * @param props.overlay - Menu content (typically a BTMenu component).
 * @param props.triggerRef - Ref to trigger element for focus return on close.
 * @param props.trigger - Trigger mode(s). Defaults to ["click"].
 * @param props.disabled - Whether the dropdown is disabled.
 * @example
 * ```tsx
 * const triggerRef = useRef<HTMLElement>(null);
 *
 * <BTDropdown
 *   testid="actions-dropdown"
 *   triggerRef={triggerRef}
 *   overlay={
 *     <BTMenu testid="actions-menu">
 *       <BTMenu.Item testid="edit">Edit</BTMenu.Item>
 *       <BTMenu.Item testid="delete">Delete</BTMenu.Item>
 *     </BTMenu>
 *   }
 * >
 *   <BdsButton
 *     innerRef={triggerRef as RefType}
 *     testid="trigger"
 *     icon={<BdsIconDotsThree />}
 *     ariaLabel="Actions"
 *   />
 * </BTDropdown>
 * ```
 */
export const BTDropdown = (props: IBTDropdownProps) => {
    const {
        trigger = defaultTriggers,
        getPopupContainer = getClosestModal,
        align = defaultAlign,
        overlayStyle,
        overlayClassName,
        disabled,
        overlay,
        children,
        className,
        testid,
        triggerRef,
        ...otherProps
    } = props;
    const tracking = useTracking({ element: "Dropdown Menu", uniqueId: testid });
    const [visible, setVisible] = useState<boolean>(false);
    const fallbackRef = useRef<HTMLElement>(null);

    // Extra RAF delay gives rc-align time to complete first-open popup positioning
    // before useMenuFocusOnOpen's double-RAF fires menu.focus(). On first open the
    // popup starts at top:0; without this delay, focus() scrolls to the page top.
    const [focusVisible, setFocusVisible] = useState(false);
    useEffect(() => {
        if (!visible) {
            setFocusVisible(false);
            return;
        }
        const id = requestAnimationFrame(() => setFocusVisible(true));
        return () => cancelAnimationFrame(id);
    }, [visible]);

    const trackedSetVisible = useCallback(
        (newVisible: boolean) => {
            // There's no 'CloseMenu' variant so we just track open.
            if (newVisible) {
                tracking.trackEvent({
                    event: "OpenMenu",
                });
            }
            setVisible(newVisible);
        },
        [tracking],
    );

    const { handleVisibleChange } = useMenuFocusOnOpen({
        triggerRef: triggerRef ?? fallbackRef,
        visible: focusVisible,
        onVisibleChange: trackedSetVisible,
    });

    // Workaround for rc-dropdown 3.2.5 bug: in controlled mode (`visible` prop),
    // clicking a menu item calls internal `setTriggerVisible(false)` but never calls
    // `onVisibleChange(false)`, so the dropdown stays open. `onOverlayClick` IS called
    // on item click, so we use it to close the dropdown.
    //
    // Skip closing when the click hits a nested BTDropdown trigger — otherwise opening
    // a nested dropdown (e.g. a BTDropdownButton rolled up into a BTOverflow menu) would
    // close this dropdown and leave the nested one orphaned in its own portal.
    //
    // rc-dropdown wires `onOverlayClick` to the overlay's onClick, which antd Menu fires
    // with a MenuInfo object (`{ key, keyPath, item, domEvent }`), not a MouseEvent — so
    // the real DOM target lives at `e.domEvent.target`.
    const handleOverlayClick = useCallback(
        (e: IRCOverlayClickEvent) => {
            const target = e.domEvent?.target;
            if (target?.closest(".BTDropdown")) {
                return;
            }
            trackedSetVisible(false);
        },
        [trackedSetVisible],
    );

    return (
        // eslint-disable-next-line react/forbid-elements
        <Dropdown
            overlay={overlay}
            getPopupContainer={getPopupContainer}
            align={align}
            visible={visible}
            overlayClassName={cn(overlayClassName, "BTDropdownOverlay")}
            disabled={disabled}
            trigger={trigger}
            // rc-dropdown applies opacity 0 to the overlay when it's hidden, but this causes issues with testing-library's `toBeVisible` assertion. Setting opacity to 1 here and relying on display: none for visibility control instead.
            overlayStyle={{ opacity: 1, ...overlayStyle }}
            className={cn(className, "BTDropdown")}
            onVisibleChange={handleVisibleChange}
            data-testid={testid}
            // We have to use otherProps here pending https://github.com/react-component/dropdown/pull/202
            {...otherProps}
            // rc-dropdown supports onOverlayClick but antd's DropDownProps doesn't expose it.
            // This is needed to close the dropdown on item click in controlled mode (visible prop).
            {...({ onOverlayClick: handleOverlayClick } as Record<string, unknown>)}
        >
            {children}
        </Dropdown>
    );
};
